"use client";

import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import * as React from "react";

import {
  LocationsMultiSelect,
  LocationsSingleSelect,
} from "#/components/selectors/locations-select";
import useLocationOptions from "#/hooks/data/pos/use-location-options";
import { cn } from "#/lib/utils";
import { Popover, PopoverTrigger } from "#/ui/popover";
import { twMerge } from "tailwind-merge";

import { ChevronsUpDown } from "lucide-react";

import { Button } from "#/ui/button";
import { PopoverContent } from "#/ui/popover";
import { useAtomValue } from "jotai";
import { atoms } from "#/lib/atoms/atoms";
import { useCallback, useMemo } from "react";
import { useOrgs } from "#/hooks/data/user/use-orgs";
import { useOnLogin } from "#/hooks/use-on-login";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "#/ui/command";
import { useTStream } from "#/hooks/use-t-stream";

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;
const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
    titleClassName?: string;
    header?: React.ReactElement | string;
    rightView?: React.ReactElement;
    hideIcon?: boolean;
    title?: string;
    chevronClassName?: string;
  }
>(
  (
    {
      className,
      chevronClassName,
      header,
      title,
      rightView,
      children,
      titleClassName,
      hideIcon,
      ...props
    },
    ref,
  ) => (
    <div className="relative">
      {rightView && (
        <div
          className={twMerge(
            "truncate z-10 text-muted text-[11px] absolute font-circular-std font-500 right-[0.625rem] top-[-7px] px-1 bg-card",
            titleClassName,
          )}
        >
          {rightView}
        </div>
      )}
      <SelectPrimitive.Trigger
        ref={ref}
        className={cn(
          "flex w-full relative items-center text-sm justify-between rounded-lg text-foreground h-10 font-400 border border-border bg-card px-3 py-2 ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:bg-background [&>span]:line-clamp-1",
          className,
        )}
        {...props}
      >
        {(title || header) && (
          <p
            className={twMerge(
              "truncate text-muted text-[11px] absolute font-circular-std font-500 left-[0.625rem] top-[-7px] px-1 bg-card z-10",
              titleClassName,
            )}
          >
            {title || header}
          </p>
        )}
        {children}
        {!hideIcon && (
          <SelectPrimitive.Icon asChild>
            <ChevronDown
              className={cn("h-4 w-4 opacity-50", chevronClassName)}
            />
          </SelectPrimitive.Icon>
        )}
      </SelectPrimitive.Trigger>
    </div>
  ),
);

SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className,
    )}
    {...props}
  >
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className,
    )}
    {...props}
  >
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        "relative rounded-xl z-50 max-h-96 min-w-[8rem] overflow-hidden border bg-card text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",

        position === "popper" &&
          "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className,
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          "p-1",
          position === "popper" &&
            "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]",
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn("py-1.5 pl-8 pr-2 text-sm font-400", className)}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-primary focus:text-primary-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className,
    )}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>

    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-muted", className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

const ComposedSelect = ({
  title,
  className,
  placeholder,
  data,
  disabled,
  onChange,
  titleClassName,
  value,
  rightView,
  hideIcon,
  hideSearch,
  ariaLabel,
  icon: Icon = ChevronsUpDown,
}: {
  title?: string | React.ReactElement;
  placeholder?: string;
  /**
   * Accessible name for the trigger. `combobox` does not take its name from
   * its contents, so without this the trigger is unaddressable by name — the
   * floating `title` is a sibling paragraph, not a label.
   */
  ariaLabel?: string;
  titleClassName?: string;
  disabled?: boolean;
  trigger?: React.ReactElement;
  rightView?: React.ReactElement;
  className?: string;
  data: { value: string; label: string }[];
  onChange(value: string): void;
  value: string;
  hideIcon?: boolean;
  hideSearch?: boolean;
  icon?: React.ElementType;
}) => {
  const [open, setOpen] = React.useState(false);

  const selectedItem = data?.find((item) => item.value === value);
  const displayText = selectedItem?.label || placeholder;

  const onOpenAutoFocus = React.useCallback(
    (event) => event.preventDefault(),
    [],
  );

  return (
    <Popover open={open} modal onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <div className="relative">
          {title && (
            <p
              className={twMerge(
                "truncate text-muted text-xs absolute font-circular-std font-500 left-[1rem] top-[-0.75rem] bg-card z-10",
                titleClassName,
              )}
            >
              {title}
            </p>
          )}
          {rightView && (
            <div
              className={twMerge(
                "text-400 truncate z-50 text-muted text-sm absolute font-circular-std font-500 right-[1.5rem] top-[-0.75rem] bg-card z-10",
                titleClassName,
              )}
            >
              {rightView}
            </div>
          )}
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-label={ariaLabel}
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              "flex w-full items-center text-base justify-between rounded-2xl text-foreground min-h-[3.2rem] pl-2 font-400 border-[1.5px] border-border px-3 py-2",
              className,
              {
                "text-muted": !value,
              },
            )}
          >
            <span className="truncate">{displayText}</span>
            {!hideIcon && <Icon className="ml-2 h-4 w-4 shrink-0 opacity-50" />}
          </Button>
        </div>
      </PopoverTrigger>
      <PopoverContent
        onOpenAutoFocus={onOpenAutoFocus}
        className="w-[var(--radix-popover-trigger-width)] p-0"
      >
        <Command
          filter={(itemValue, search) => {
            const label = data
              .find((item) => item.value === itemValue)
              ?.label?.toLowerCase();
            if (label?.includes(search?.toLowerCase())) return 1;
            return 0;
          }}
        >
          {!hideSearch && (
            <CommandInput
              placeholder={`Search ${placeholder?.toLowerCase() || ""}...`}
            />
          )}
          <CommandEmpty>No results found.</CommandEmpty>
          <CommandGroup className="max-h-[250px] overflow-auto">
            {data?.map((item) => (
              <CommandItem
                key={item.value}
                value={item.value}
                onSelect={(currentValue) => {
                  onChange(currentValue);
                  setOpen(false);
                }}
                className={cn(
                  "cursor-pointer",
                  value === item.value && "bg-accent",
                )}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    value === item.value ? "opacity-100" : "opacity-0",
                  )}
                />
                {item.label}
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const MultiLocationsSelect = ({
  selectedLocationIds,
  setSelectedLocationIds,
  enableSelectAll = false,
  title = "",
  className = "",
  classNameTitle = "",
  onCloseAutoFocus = () => {},
  multiSelect = true,
  disabled = false,
  placeholder,
  selectedBadgeVariant,
  enableDspFilterTabs = false,
}: {
  selectedLocationIds: string[];
  setSelectedLocationIds: (ids: string[]) => void;
  enableSelectAll?: boolean;
  title?: string;
  className?: string;
  classNameTitle?: string;
  onCloseAutoFocus?: () => void;
  multiSelect?: boolean;
  disabled?: boolean;
  children?: React.ReactNode;
  placeholder?: string;
  selectedBadgeVariant?: React.ComponentProps<
    typeof LocationsMultiSelect
  >["selectedBadgeVariant"];
  enableDspFilterTabs?: boolean;
}) => {
  const { t } = useTStream("common");
  const { options, dspConnectedOptions } = useLocationOptions();
  const [showDSPOnly, setShowDSPOnly] = React.useState(false);

  const onFilter = React.useCallback(
    (ids: string[]) => {
      if (multiSelect) {
        setSelectedLocationIds(ids);
      } else {
        setSelectedLocationIds([ids?.[0]]);
      }
    },
    [setSelectedLocationIds, multiSelect],
  );

  return (
    <LocationsMultiSelect
      options={
        enableDspFilterTabs && showDSPOnly ? dspConnectedOptions : options
      }
      selectedLocationIds={selectedLocationIds}
      onChange={onFilter}
      enableSelectAll={enableSelectAll}
      title={title}
      className={className}
      classNameTitle={classNameTitle}
      onCloseAutoFocus={onCloseAutoFocus}
      disabled={disabled}
      placeholder={placeholder}
      selectedBadgeVariant={selectedBadgeVariant}
      filterTabs={
        enableDspFilterTabs
          ? [
              { value: "all", label: t("All Locations") },
              { value: "dsp", label: t("DSP Connected Only") },
            ]
          : undefined
      }
      activeFilterTab={
        enableDspFilterTabs ? (showDSPOnly ? "dsp" : "all") : undefined
      }
      onFilterTabChange={
        enableDspFilterTabs ? (tab) => setShowDSPOnly(tab === "dsp") : undefined
      }
    />
  );
};

const LocationsSelect = ({
  selectedLocationIds,
  setSelectedLocationIds,
  multiSelect = true,
  titleClassName = "",
  title = "",
  placeholder,
}) => {
  const { options } = useLocationOptions();

  return (
    <LocationsSingleSelect
      options={options}
      selectedLocationIds={selectedLocationIds}
      onChange={setSelectedLocationIds}
      multiSelect={multiSelect}
      title={title}
      titleClassName={titleClassName}
      placeholder={placeholder}
    />
  );
};

const MerchantSelect = ({
  className,
  placeholder,
  disabled,
  titleClassName,
}: {
  placeholder?: string;
  titleClassName?: string;
  disabled?: boolean;
  className?: string;
}) => {
  const value = useAtomValue(atoms.mid);
  const { data: orgs } = useOrgs();
  const orgId = useAtomValue(atoms.orgId);
  const { setCurrentOrgMerchant } = useOnLogin();

  // Route the in-page merchant switch through the same flow the profile
  // menu uses (`setCurrentOrgMerchant`). Setting `atoms.mid` directly leaves
  // the `merchant_id` cookie pointing at the previous merchant — and any
  // queryFn that reads context() from cookies (e.g. `/dsp/locations`) will
  // then return data for the OLD merchant joined against the NEW merchant's
  // location ids, surfacing as empty DSP badges. See plan:
  // fix-dsp-connections-after-in-page-merchant-switch.
  const onChange = useCallback(
    (next: string) => {
      if (!next || next === value) return;
      setCurrentOrgMerchant({ org_id: orgId, merchant_id: next });
    },
    [orgId, value, setCurrentOrgMerchant],
  );

  const data = useMemo(() => {
    return orgs
      .find((o) => o?._id === orgId)
      ?.merchants?.map((m) => ({
        value: m._id,
        label: m.name,
        image: m?.image_url,
      }));
  }, [orgs, orgId]);

  const merchant = data?.find((m) => m.value === value);

  return (
    <Select onValueChange={onChange} value={value}>
      <SelectTrigger
        disabled={disabled}
        titleClassName={titleClassName}
        className={twMerge(
          "items-center h-10 space-x-3 text-sm text-center",
          className,
        )}
      >
        <div className="border-[1.5px] flex border-border rounded-full overflow-hidden items-center">
          {merchant?.image ? (
            <img
              src={merchant?.image}
              alt={merchant?.label}
              className="h-6 w-6"
            />
          ) : (
            <div className="h-6 w-6 bg-border" />
          )}
        </div>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="max-h-[20rem]">
        <SelectGroup>
          {data?.map((item) => {
            return (
              <SelectItem
                onClick={(e) => e?.stopPropagation()}
                key={item.value}
                value={item.value}
              >
                <p> {item.label}</p>
              </SelectItem>
            );
          })}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
};

export {
  ComposedSelect,
  LocationsSelect,
  MerchantSelect,
  MultiLocationsSelect,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
